import { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import * as LucideIcons from "lucide-react";
import {
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  Download,
  Send,
  UserPlus,
  X,
  Search,
  ExternalLink,
  Loader2,
  CheckSquare,
  Star,
  FileText,
  Sparkles,
  CheckCircle2,
  LayoutDashboard,
  Users,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/DatePicker";
import { EmailBodyEditor } from "@/components/recruitment/EmailBodyEditor";
import { AddCandidateDialog } from "@/components/recruitment/AddCandidateDialog";
import { useAuth } from "@/contexts/AuthContext";

// Curated icon set for job ads
const JOB_AD_ICONS = [
  "Briefcase","Code","Palette","BarChart","Users","Megaphone","Wrench","Database",
  "Globe","Microscope","BookOpen","Calculator","Camera","Cloud","Cpu","DollarSign",
  "Edit","FileText","Headphones","Heart","Home","Laptop","Layers","Lightbulb",
  "Mail","Map","Monitor","Music","Package","Phone","PenTool","Rocket","Settings",
  "Shield","ShoppingCart","Star","Truck","Tv","Video","Wifi","Zap","Building",
  "Stethoscope","GraduationCap","Hammer","FlaskConical","LeafyGreen","Plane",
] as const;

function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (LucideIcons as any)[name] as React.ElementType | undefined;
  if (!Icon) return <Briefcase className={className} />;
  return <Icon className={className} />;
}
import { toast } from "sonner";
import {
  apiGetRecruitmentPositions,
  apiCreateRecruitmentPosition,
  apiUpdateRecruitmentPosition,
  apiDeleteRecruitmentPosition,
  apiGeneratePositionContent,
  apiGetRecruitmentCustomFields,
  apiCreateRecruitmentCustomField,
  apiUpdateRecruitmentCustomField,
  apiDeleteRecruitmentCustomField,
  apiGetRecruitmentApplications,
  apiGetRecruitmentApplication,
  apiDeleteRecruitmentApplication,
  apiUpdateRecruitmentApplicationStatus,
  apiBulkUpdateRecruitmentApplicationStatus,
  apiUpdateRecruitmentApplicationNotes,
  apiAssignRecruitmentInterview,
  apiDeleteRecruitmentInterview,
  apiAnalyzeApplication,
  apiGetRecruitmentEmailTemplates,
  apiCreateRecruitmentEmailTemplate,
  apiUpdateRecruitmentEmailTemplate,
  apiDeleteRecruitmentEmailTemplate,
  apiDownloadRecruitmentResume,
  apiGetDepartments,
  apiGetEmployees,
  getTenantSlug,
  apiGetCandidateProfile,
  apiUpsertCandidateProfile,
  apiGetProfileComments,
  apiAddProfileComment,
  apiDeleteProfileComment,
  apiGetInterviewScorecard,
  apiUpsertInterviewScorecard,
  apiConvertApplicantToEmployee,
  apiSendPreboarding,
  apiGetRecruitmentDashboardStats,
  type RecruitmentPosition,
  type RecruitmentCustomField,
  type RecruitmentApplication,
  type RecruitmentApplicationDetail,
  type RecruitmentEmailTemplate,
  type RecruitmentInterview,
  type RecruitmentCandidateProfile,
  type RecruitmentInterviewScorecard,
  type RecruitmentProfileComment,
} from "@/lib/api";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getStatusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    new: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    screening: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    interview: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    offer: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    hired: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    withdrawn: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400",
  };
  return map[status] ?? "bg-gray-100 text-gray-800";
}

function getPositionStatusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    open: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400",
    on_hold: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    closed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  };
  return map[status] ?? "bg-gray-100 text-gray-800";
}

function getDeadlineDaysLeft(deadline: string): number {
  const d = new Date(deadline);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function DeadlineBadge({ deadline }: { deadline: string }) {
  const { t } = useTranslation();
  const daysLeft = getDeadlineDaysLeft(deadline);
  const formatted = new Date(deadline).toLocaleDateString();

  if (daysLeft < 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
        {t("recruitment.dashboard.expired")}
      </span>
    );
  }
  if (daysLeft <= 3) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
        {t("recruitment.dashboard.closingSoon")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
      {t("recruitment.dashboard.closesOn", { date: formatted })}
    </span>
  );
}

const APPLICATION_STATUS_COLORS: Record<string, string> = {
  new: "#3b82f6",
  screening: "#eab308",
  interview: "#a855f7",
  offer: "#f97316",
  hired: "#22c55e",
  rejected: "#ef4444",
  withdrawn: "#9ca3af",
};

function resolvePreview(
  template: RecruitmentEmailTemplate,
  app: RecruitmentApplicationDetail | null,
  positionTitle: string,
  statusLabel: string,
): { subject: string; body: string } {
  const vars: Record<string, string> = {
    candidate_name: app ? `${app.firstName} ${app.lastName}` : "Candidate Name",
    position_title: positionTitle || "Position Title",
    company_name: "Our Company",
    status: statusLabel,
  };
  let subject = template.subject;
  let body = template.bodyHtml;
  for (const [k, v] of Object.entries(vars)) {
    subject = subject.split(`{{${k}}}`).join(v);
    body = body.split(`{{${k}}}`).join(v);
  }
  return { subject, body };
}

// ── Main Component ────────────────────────────────────────────────────────────

const Recruitment = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = tabParam === "applications" || tabParam === "emailTemplates" || tabParam === "positions"
    ? tabParam
    : "dashboard";

  function handleTabChange(tab: string) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next);
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <Briefcase className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("recruitment.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("recruitment.subtitle")}</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="dashboard" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <LayoutDashboard className="h-3.5 w-3.5" />
              {t("recruitment.tabs.dashboard")}
            </TabsTrigger>
            <TabsTrigger value="positions" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Briefcase className="h-3.5 w-3.5" />
              {t("recruitment.tabs.positions")}
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <UserPlus className="h-3.5 w-3.5" />
              {t("recruitment.tabs.applications")}
            </TabsTrigger>
            <TabsTrigger value="emailTemplates" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Send className="h-3.5 w-3.5" />
              {t("recruitment.tabs.emailTemplates")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="mt-4">
            <DashboardTab />
          </TabsContent>
          <TabsContent value="positions" className="mt-4">
            <PositionsTab />
          </TabsContent>
          <TabsContent value="applications" className="mt-4">
            <ApplicationsTab />
          </TabsContent>
          <TabsContent value="emailTemplates" className="mt-4">
            <EmailTemplatesTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Recruitment;

// ── Dashboard Tab ─────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: stats, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["recruitment-dashboard-stats"],
    queryFn: apiGetRecruitmentDashboardStats,
  });

  function viewPosition(positionId: number) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "positions");
    next.set("positionId", String(positionId));
    setSearchParams(next);
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-border/60 bg-card shadow-sm p-4">
            <div className="h-4 w-24 bg-muted rounded animate-pulse mb-3" />
            <div className="h-8 w-16 bg-muted rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (isError || !stats) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm px-6 py-10 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-3" />
        <h3 className="font-semibold">{t("recruitment.dashboard.loadErrorTitle")}</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">{t("recruitment.dashboard.loadErrorDescription")}</p>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          {t("recruitment.dashboard.retry")}
        </Button>
      </div>
    );
  }

  const applicationsByStatusData = stats.applicationsByStatus.map((s) => ({
    status: s.status,
    name: t(`recruitment.applications.statuses.${s.status}`, { defaultValue: s.status }),
    count: s.count,
  }));

  const applicationsPerPositionData = stats.applicationsPerPosition.map((p) => ({
    title: p.title.length > 18 ? `${p.title.slice(0, 16)}…` : p.title,
    fullTitle: p.title,
    count: p.applicationCount,
  }));

  const kpiCards = [
    { label: t("recruitment.dashboard.openPositions"), value: stats.totalOpenPositions, icon: Briefcase, color: "text-blue-600 bg-blue-500/10" },
    { label: t("recruitment.dashboard.totalCandidates"), value: stats.totalCandidates, icon: Users, color: "text-purple-600 bg-purple-500/10" },
    { label: t("recruitment.dashboard.newApplications"), value: stats.newApplicationsLast7Days, icon: UserPlus, color: "text-green-600 bg-green-500/10" },
    { label: t("recruitment.dashboard.closingSoon"), value: stats.positionsClosingSoon.length, icon: Clock, color: "text-amber-600 bg-amber-500/10" },
  ];

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <div key={card.label} className="rounded-2xl border border-border/60 bg-card shadow-sm p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{card.label}</p>
                <p className="text-3xl font-bold mt-1">{card.value}</p>
              </div>
              <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${card.color}`}>
                <card.icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-4">
          <h3 className="text-sm font-semibold mb-3">{t("recruitment.dashboard.applicationsPerPosition")}</h3>
          {applicationsPerPositionData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">{t("recruitment.dashboard.noOpenPositions")}</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={applicationsPerPositionData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="title" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={32} />
                <RechartsTooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 13 }}
                  labelStyle={{ fontWeight: 600 }}
                  formatter={(value: number, _name, item: any) => [value, item?.payload?.fullTitle ?? ""]}
                />
                <Bar dataKey="count" name={t("recruitment.applications.title")} fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-4">
          <h3 className="text-sm font-semibold mb-3">{t("recruitment.dashboard.applicationPipeline")}</h3>
          {applicationsByStatusData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">{t("recruitment.dashboard.noApplications")}</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={applicationsByStatusData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {applicationsByStatusData.map((entry) => (
                    <Cell key={entry.status} fill={APPLICATION_STATUS_COLORS[entry.status] ?? "#9ca3af"} />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 13 }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Closing soon */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t("recruitment.dashboard.closingSoonTitle")}
          </h3>
        </div>
        {stats.positionsClosingSoon.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t("recruitment.dashboard.noClosingSoon")}</p>
        ) : (
          <Table>
            <TableBody>
              {stats.positionsClosingSoon.map((p) => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-muted/40" onClick={() => viewPosition(p.id)}>
                  <TableCell className="py-3 pl-4 font-medium text-sm">{p.title}</TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground">{new Date(p.applicationDeadline).toLocaleDateString()}</TableCell>
                  <TableCell className="py-3"><DeadlineBadge deadline={p.applicationDeadline} /></TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground text-center">{p.applicationCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ── Positions Tab ─────────────────────────────────────────────────────────────

function PositionsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<RecruitmentPosition | null>(null);

  // Auto-open new position dialog when navigated from Quick Actions
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      setEditingPosition(null);
      setSheetOpen(true);
    }
  }, [searchParams]);

  const { data: positions = [], isLoading } = useQuery({
    queryKey: ["recruitment-positions"],
    queryFn: apiGetRecruitmentPositions,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
  });

  useEffect(() => {
    const positionId = Number(searchParams.get("positionId"));
    if (!Number.isFinite(positionId) || positionId <= 0 || positions.length === 0) return;
    const target = positions.find((position) => position.id === positionId);
    if (target) {
      setEditingPosition(target);
      setSheetOpen(true);
    }
  }, [positions, searchParams]);

  function openPosition(position: RecruitmentPosition) {
    setEditingPosition(position);
    setSheetOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("tab", "positions");
    next.set("positionId", String(position.id));
    setSearchParams(next);
  }

  function handlePositionDialogOpenChange(open: boolean) {
    setSheetOpen(open);
    if (!open) {
      const next = new URLSearchParams(searchParams);
      next.delete("positionId");
      setSearchParams(next);
    }
  }

  const deleteMutation = useMutation({
    mutationFn: apiDeleteRecruitmentPosition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-positions"] });
      queryClient.invalidateQueries({ queryKey: ["recruitment-dashboard-stats"] });
      toast.success(t("recruitment.positions.deleteConfirm", { title: "" }).replace('"?"', ""));
    },
    onError: (err: any) => toast.error(err.message),
  });

  function copyApplyLink(pos: RecruitmentPosition) {
    if (!pos.publicSlug) return;
    const url = `${window.location.origin}/apply/${pos.publicSlug}`;
    navigator.clipboard.writeText(url).then(() => toast.success(t("recruitment.positions.linkCopied")));
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditingPosition(null); setSheetOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          {t("recruitment.positions.new")}
        </Button>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
              <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("recruitment.positions.jobTitle")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("recruitment.positions.department")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("recruitment.positions.jobType")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("recruitment.positions.status")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden xl:table-cell text-center">{t("recruitment.positions.applicationsCount")}</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
                </TableCell>
              </TableRow>
            ) : positions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  {t("recruitment.positions.noPositions")}
                </TableCell>
              </TableRow>
            ) : (
              positions.map((pos) => (
                <TableRow
                  key={pos.id}
                  className="border-b border-border/40 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => openPosition(pos)}
                >
                  <TableCell className="pl-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <DynamicIcon name={pos.iconName ?? "Briefcase"} className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium">{pos.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 hidden md:table-cell">
                    <span className="text-sm text-muted-foreground">{pos.departmentName ?? "—"}</span>
                  </TableCell>
                  <TableCell className="py-3 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground">
                      {t(`recruitment.positions.jobTypes.${pos.jobType}`, { defaultValue: pos.jobType })}
                    </span>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPositionStatusBadgeClass(pos.status)}`}>
                        {t(`recruitment.positions.statuses.${pos.status}`, { defaultValue: pos.status })}
                      </span>
                      {pos.applicationDeadline && <DeadlineBadge deadline={pos.applicationDeadline} />}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 hidden xl:table-cell text-center">
                    <span className="text-sm text-muted-foreground">{pos.applicationCount}</span>
                  </TableCell>
                  <TableCell className="py-3 w-20" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5 pr-2">
                      <button
                        onClick={() => copyApplyLink(pos)}
                        title={t("recruitment.positions.copyLink")}
                        disabled={!pos.publicSlug}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t("recruitment.positions.deleteConfirm", { title: pos.title }))) {
                            deleteMutation.mutate(pos.id);
                          }
                        }}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PositionDialog
        open={sheetOpen}
        onOpenChange={handlePositionDialogOpenChange}
        position={editingPosition}
        departments={departments}
      />
    </div>
  );
}

// ── Position Dialog ────────────────────────────────────────────────────────────

interface PositionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: RecruitmentPosition | null;
  departments: { id: number; name: string }[];
}

interface LocalCustomField {
  id?: number;
  fieldLabel: string;
  fieldType: string;
  fieldOptions: string;
  isRequired: boolean;
  sortOrder: number;
  _deleted?: boolean;
}

function PositionDialog({ open, onOpenChange, position, departments }: PositionSheetProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(position?.title ?? "");
  const [departmentId, setDepartmentId] = useState(position?.departmentId ? String(position.departmentId) : "");
  const [description, setDescription] = useState(position?.description ?? "");
  const [requirements, setRequirements] = useState(position?.requirements ?? "");
  const [location, setLocation] = useState(position?.location ?? "");
  const [jobType, setJobType] = useState(position?.jobType ?? "full_time");
  const [status, setStatus] = useState(position?.status ?? "draft");
  const [iconName, setIconName] = useState(position?.iconName ?? "Briefcase");
  const [termsAndConditions, setTermsAndConditions] = useState(position?.termsAndConditions ?? "");
  const [privacyPolicy, setPrivacyPolicy] = useState(position?.privacyPolicy ?? "");
  const [applicationDeadline, setApplicationDeadline] = useState(position?.applicationDeadline ?? "");
  const [iconSearch, setIconSearch] = useState("");
  const [customFields, setCustomFields] = useState<LocalCustomField[]>([]);
  const [keywords, setKeywords] = useState<{ keyword: string; weight: 1 | 2 | 3 }[]>([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState<"description" | "requirements" | null>(null);
  const [initialized, setInitialized] = useState(false);

  const { data: existingFields = [] } = useQuery({
    queryKey: ["recruitment-custom-fields", position?.id],
    queryFn: () => apiGetRecruitmentCustomFields(position!.id),
    enabled: open && !!position,
  });

  // Re-initialize form when sheet opens
  if (open && !initialized) {
    setInitialized(true);
    setTitle(position?.title ?? "");
    setDepartmentId(position?.departmentId ? String(position.departmentId) : "");
    setDescription(position?.description ?? "");
    setRequirements(position?.requirements ?? "");
    setLocation(position?.location ?? "");
    setJobType(position?.jobType ?? "full_time");
    setStatus(position?.status ?? "draft");
    setIconName(position?.iconName ?? "Briefcase");
    setTermsAndConditions(position?.termsAndConditions ?? "");
    setPrivacyPolicy(position?.privacyPolicy ?? "");
    setApplicationDeadline(position?.applicationDeadline ?? "");
    setKeywords(position?.keywords ?? []);
    setIconSearch("");
    setCustomFields([]);
  }
  if (!open && initialized) setInitialized(false);

  // Populate custom fields when they load
  if (open && position && existingFields.length > 0 && customFields.length === 0 && initialized) {
    setCustomFields(
      existingFields.map((f) => ({
        id: f.id,
        fieldLabel: f.fieldLabel,
        fieldType: f.fieldType,
        fieldOptions: Array.isArray(f.fieldOptions) ? f.fieldOptions.join(", ") : "",
        isRequired: f.isRequired,
        sortOrder: f.sortOrder,
      })),
    );
  }

  const createMutation = useMutation({
    mutationFn: apiCreateRecruitmentPosition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-positions"] });
      queryClient.invalidateQueries({ queryKey: ["recruitment-dashboard-stats"] });
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof apiUpdateRecruitmentPosition>[1] }) =>
      apiUpdateRecruitmentPosition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-positions"] });
      queryClient.invalidateQueries({ queryKey: ["recruitment-dashboard-stats"] });
    },
  });

  function addField() {
    setCustomFields((prev) => [
      ...prev,
      { fieldLabel: "", fieldType: "text", fieldOptions: "", isRequired: false, sortOrder: prev.length },
    ]);
  }

  function updateField(idx: number, patch: Partial<LocalCustomField>) {
    setCustomFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function removeField(idx: number) {
    setCustomFields((prev) => {
      if (prev[idx].id) return prev.map((f, i) => (i === idx ? { ...f, _deleted: true } : f));
      return prev.filter((_, i) => i !== idx);
    });
  }

  function moveField(idx: number, dir: -1 | 1) {
    setCustomFields((prev) => {
      const visible = prev.filter((f) => !f._deleted);
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= visible.length) return prev;
      const next = [...visible];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next.map((f, i) => ({ ...f, sortOrder: i }));
    });
  }

  async function handleGenerate(field: "description" | "requirements") {
    if (!title.trim()) { toast.error("Enter a job title first"); return; }
    setGenerating(field);
    try {
      const dept = departments.find((d) => String(d.id) === departmentId)?.name;
      const { text } = await apiGeneratePositionContent({ field, title, department: dept, location, jobType, language: i18n.language });
      if (field === "description") setDescription(text);
      else setRequirements(text);
    } catch {
      toast.error("AI generation failed. Please try again.");
    } finally {
      setGenerating(null);
    }
  }

  async function handleSave() {
    if (!title.trim()) { toast.error(t("recruitment.positions.jobTitle") + " " + t("recruitment.apply.required")); return; }
    setSaving(true);
    try {
      const posData = {
        title: title.trim(),
        departmentId: departmentId ? Number(departmentId) : null,
        description: description.trim() || undefined,
        requirements: requirements.trim() || undefined,
        location: location.trim() || undefined,
        jobType,
        status,
        iconName,
        termsAndConditions: termsAndConditions.trim() || null,
        privacyPolicy: privacyPolicy.trim() || null,
        applicationDeadline: applicationDeadline || null,
        keywords: keywords.filter((k) => k.keyword.trim()).map((k) => ({ keyword: k.keyword.trim(), weight: k.weight })),
      };

      let savedId: number;
      if (position) {
        const updated = await updateMutation.mutateAsync({ id: position.id, data: posData });
        savedId = updated.id;
      } else {
        const created = await createMutation.mutateAsync(posData);
        savedId = created.id;
      }

      const visibleFields = customFields.filter((f) => !f._deleted);
      for (const f of customFields) {
        const opts = f.fieldOptions.split(",").map((s) => s.trim()).filter(Boolean);
        if (f._deleted && f.id) { await apiDeleteRecruitmentCustomField(savedId, f.id); continue; }
        if (!f.fieldLabel.trim()) continue;
        const fieldData = { fieldLabel: f.fieldLabel.trim(), fieldType: f.fieldType, fieldOptions: opts, isRequired: f.isRequired, sortOrder: visibleFields.indexOf(f) };
        if (f.id) await apiUpdateRecruitmentCustomField(savedId, f.id, fieldData);
        else await apiCreateRecruitmentCustomField(savedId, fieldData);
      }

      queryClient.invalidateQueries({ queryKey: ["recruitment-positions"] });
      toast.success(position ? t("recruitment.positions.saveChanges") : t("recruitment.positions.create"));
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  const visibleFields = customFields.filter((f) => !f._deleted);

  const JOB_TYPE_VALUES = ["full_time", "part_time", "contract", "internship", "remote"];
  const POSITION_STATUS_VALUES = ["draft", "open", "on_hold", "closed"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle>{position ? t("recruitment.positions.edit") : t("recruitment.positions.new")}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-5">

            {/* Two-column main layout */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">

              {/* ── Left column: structural fields + icon ── */}
              <div className="space-y-4">
                <FloatField
                  label={t("recruitment.positions.jobTitle")}
                  required
                  value={title}
                  onChange={(v) => setTitle(v as string)}
                  placeholder={t("recruitment.positions.titlePlaceholder")}
                />

                <FloatWrapper label={t("recruitment.positions.department")}>
                  <Select value={departmentId || "__none__"} onValueChange={(v) => setDepartmentId(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                      <SelectValue placeholder={t("recruitment.positions.noDepartment")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("recruitment.positions.noDepartment")}</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FloatWrapper>

                <FloatField
                  label={t("recruitment.positions.location")}
                  value={location}
                  onChange={(v) => setLocation(v as string)}
                  placeholder={t("recruitment.positions.locationPlaceholder")}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FloatWrapper label={t("recruitment.positions.jobType")}>
                    <Select value={jobType} onValueChange={setJobType}>
                      <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {JOB_TYPE_VALUES.map((v) => (
                          <SelectItem key={v} value={v}>{t(`recruitment.positions.jobTypes.${v}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FloatWrapper>
                  <FloatWrapper label={t("recruitment.positions.status")}>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITION_STATUS_VALUES.map((v) => (
                          <SelectItem key={v} value={v}>{t(`recruitment.positions.statuses.${v}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FloatWrapper>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{t("recruitment.positions.applicationDeadline")}</Label>
                  <div className="flex items-center gap-2">
                    <DatePicker value={applicationDeadline} onChange={setApplicationDeadline} className="h-10" />
                    {applicationDeadline && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setApplicationDeadline("")}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t("recruitment.positions.applicationDeadlineHint")}</p>
                </div>

                {/* Icon Picker */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">{t("recruitment.positions.icon")}</Label>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <DynamicIcon name={iconName} className="h-6 w-6 text-primary" />
                    </div>
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        placeholder={t("recruitment.positions.iconSearch")}
                        className="pl-8 h-9 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-8 gap-1 p-2 border rounded-lg max-h-36 overflow-y-auto bg-muted/20">
                    {JOB_AD_ICONS
                      .filter((n) => !iconSearch || n.toLowerCase().includes(iconSearch.toLowerCase()))
                      .map((n) => (
                        <button
                          key={n}
                          type="button"
                          title={n}
                          onClick={() => setIconName(n)}
                          className={`p-1.5 rounded-md flex items-center justify-center transition-colors hover:bg-primary/10 ${
                            iconName === n ? "bg-primary/20 ring-1 ring-primary" : ""
                          }`}
                        >
                          <DynamicIcon name={n} className="h-4 w-4" />
                        </button>
                      ))}
                  </div>
                </div>
              </div>

              {/* ── Right column: text content + legal ── */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{t("recruitment.positions.description")}</span>
                    <button
                      type="button"
                      onClick={() => handleGenerate("description")}
                      disabled={generating !== null}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 hover:border-violet-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800 dark:hover:bg-violet-900/40"
                    >
                      {generating === "description"
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Sparkles className="h-3.5 w-3.5" />}
                      Generate with Sara
                    </button>
                  </div>
                  <FloatField
                    label=""
                    type="textarea"
                    value={description}
                    onChange={(v) => setDescription(v as string)}
                    placeholder={t("recruitment.positions.descriptionPlaceholder")}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{t("recruitment.positions.requirements")}</span>
                    <button
                      type="button"
                      onClick={() => handleGenerate("requirements")}
                      disabled={generating !== null}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 hover:border-violet-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800 dark:hover:bg-violet-900/40"
                    >
                      {generating === "requirements"
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Sparkles className="h-3.5 w-3.5" />}
                      Generate with Sara
                    </button>
                  </div>
                  <FloatField
                    label=""
                    type="textarea"
                    value={requirements}
                    onChange={(v) => setRequirements(v as string)}
                    placeholder={t("recruitment.positions.requirementsPlaceholder")}
                  />
                </div>

                {/* Legal / T&C */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">{t("recruitment.positions.legalSection")}</p>
                  <FloatField
                    label={t("recruitment.positions.termsAndConditions")}
                    type="textarea"
                    value={termsAndConditions}
                    onChange={(v) => setTermsAndConditions(v as string)}
                    placeholder={t("recruitment.positions.termsAndConditionsPlaceholder")}
                  />
                  <FloatField
                    label={t("recruitment.positions.privacyPolicy")}
                    type="textarea"
                    value={privacyPolicy}
                    onChange={(v) => setPrivacyPolicy(v as string)}
                    placeholder={t("recruitment.positions.privacyPolicyPlaceholder")}
                  />
                </div>
              </div>
            </div>

            <Separator className="my-5" />

            {/* AI Keyword Scoring — full width */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">AI Keyword Scoring</p>
                  <p className="text-xs text-muted-foreground">Keywords the AI will look for in applicant CVs to generate a match score</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setKeywords((prev) => [...prev, { keyword: "", weight: 2 }])}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Keyword
                </Button>
              </div>

              {keywords.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4 border rounded-md border-dashed">
                  No keywords yet. Add keywords to enable AI CV scoring for this position.
                </p>
              )}

              <div className="space-y-2">
                {keywords.map((kw, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={kw.keyword}
                      onChange={(e) => setKeywords((prev) => prev.map((k, i) => i === idx ? { ...k, keyword: e.target.value } : k))}
                      placeholder="e.g. React, Project Management, Python"
                      className="flex-1 h-8 text-sm"
                    />
                    <Select
                      value={String(kw.weight)}
                      onValueChange={(v) => setKeywords((prev) => prev.map((k, i) => i === idx ? { ...k, weight: Number(v) as 1 | 2 | 3 } : k))}
                    >
                      <SelectTrigger className="w-36 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Nice to have</SelectItem>
                        <SelectItem value="2">Important</SelectItem>
                        <SelectItem value="3">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setKeywords((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-5" />

            {/* Form Builder — full width */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{t("recruitment.customFields.title")}</p>
                  <p className="text-xs text-muted-foreground">{t("recruitment.customFields.subtitle")}</p>
                </div>
                <Button variant="outline" size="sm" onClick={addField}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {t("recruitment.customFields.addField")}
                </Button>
              </div>

              {visibleFields.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4 border rounded-md border-dashed">
                  {t("recruitment.customFields.noFields")}
                </p>
              )}

              <div className="space-y-3">
                {visibleFields.map((field, idx) => (
                  <CustomFieldRow
                    key={idx}
                    field={field}
                    index={idx}
                    total={visibleFields.length}
                    onChange={(patch) => updateField(customFields.indexOf(field), patch)}
                    onRemove={() => removeField(customFields.indexOf(field))}
                    onMove={(dir) => moveField(idx, dir)}
                  />
                ))}
              </div>
            </div>

          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("recruitment.positions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t("recruitment.positions.saving") : position ? t("recruitment.positions.saveChanges") : t("recruitment.positions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomFieldRow({
  field, index, total, onChange, onRemove, onMove,
}: {
  field: LocalCustomField;
  index: number;
  total: number;
  onChange: (patch: Partial<LocalCustomField>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  const FIELD_TYPE_VALUES = ["text", "textarea", "select", "multiselect", "file", "number", "date", "url"];
  const hasOptions = field.fieldType === "select" || field.fieldType === "multiselect";

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-0.5">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 grid grid-cols-2 gap-2">
          <Input
            value={field.fieldLabel}
            onChange={(e) => onChange({ fieldLabel: e.target.value })}
            placeholder={t("recruitment.customFields.labelPlaceholder")}
            className="h-8 text-sm"
          />
          <Select value={field.fieldType} onValueChange={(v) => onChange({ fieldType: v })}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FIELD_TYPE_VALUES.map((v) => (
                <SelectItem key={v} value={v}>{t(`recruitment.customFields.types.${v}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <Switch checked={field.isRequired} onCheckedChange={(v) => onChange({ isRequired: v })} id={`req-${index}`} />
          <Label htmlFor={`req-${index}`} className="text-xs text-muted-foreground cursor-pointer">
            {t("recruitment.customFields.required")}
          </Label>
        </div>

        <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-destructive">
          <X className="h-4 w-4" />
        </button>
      </div>

      {hasOptions && (
        <div className="space-y-1 pl-7">
          <Label className="text-xs text-muted-foreground">{t("recruitment.customFields.options")}</Label>
          <Input
            value={field.fieldOptions}
            onChange={(e) => onChange({ fieldOptions: e.target.value })}
            placeholder={t("recruitment.customFields.optionsPlaceholder")}
            className="h-8 text-sm"
          />
        </div>
      )}
    </div>
  );
}

// ── Applications Tab ──────────────────────────────────────────────────────────

type AiScoreFilter = "all" | "excellent" | "good" | "below_60" | "not_analyzed";
type ApplicationSort = "latest" | "ai_desc" | "ai_asc";

function ApplicationsTab() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canManuallyAddCandidate = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterPosition, setFilterPosition] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [aiScoreFilter, setAiScoreFilter] = useState<AiScoreFilter>("all");
  const [sortBy, setSortBy] = useState<ApplicationSort>("latest");
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteAppId, setDeleteAppId] = useState<number | null>(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [addCandidateOpen, setAddCandidateOpen] = useState(false);

  // ── Bulk selection ────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState("");

  // Clear selection whenever filters change
  useEffect(() => { setSelectedIds(new Set()); }, [filterPosition, filterStatus, aiScoreFilter]);

  useEffect(() => {
    const positionId = searchParams.get("positionId");
    if (positionId && positionId !== filterPosition) {
      setFilterPosition(positionId);
    }
  }, [filterPosition, searchParams]);

  useEffect(() => {
    const applicationId = Number(searchParams.get("applicationId"));
    if (Number.isFinite(applicationId) && applicationId > 0) {
      setSelectedAppId(applicationId);
      setDetailOpen(true);
    }
  }, [searchParams]);

  function openApplication(applicationId: number) {
    setSelectedAppId(applicationId);
    setDetailOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("tab", "applications");
    next.set("applicationId", String(applicationId));
    setSearchParams(next);
  }

  function closeApplication() {
    setDetailOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete("applicationId");
    setSearchParams(next);
    queryClient.invalidateQueries({ queryKey: ["recruitment-applications"] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteRecruitmentApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-applications"] });
      setDeleteAppId(null);
      setDeleteConfirmed(false);
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: () => apiBulkUpdateRecruitmentApplicationStatus([...selectedIds], bulkStatus),
    onSuccess: (res) => {
      toast.success(`${res.updated} applicant${res.updated !== 1 ? "s" : ""} updated to "${bulkStatus}"`);
      setSelectedIds(new Set());
      setBulkStatus("");
      queryClient.invalidateQueries({ queryKey: ["recruitment-applications"] });
    },
    onError: () => toast.error("Bulk update failed"),
  });

  const { data: positions = [] } = useQuery({
    queryKey: ["recruitment-positions"],
    queryFn: apiGetRecruitmentPositions,
  });

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ["recruitment-applications", filterPosition, filterStatus],
    queryFn: () =>
      apiGetRecruitmentApplications({
        positionId: filterPosition ? Number(filterPosition) : undefined,
        status: filterStatus || undefined,
      }),
  });

  const APP_STATUS_VALUES = ["new", "screening", "interview", "offer", "hired", "rejected", "withdrawn"];

  const visibleApplications = useMemo(() => {
    const filtered = applications.filter((app) => {
      const score = app.aiScore;
      switch (aiScoreFilter) {
        case "excellent":
          return score != null && score >= 80;
        case "good":
          return score != null && score >= 60;
        case "below_60":
          return score != null && score < 60;
        case "not_analyzed":
          return score == null;
        default:
          return true;
      }
    });

    const byLatest = (a: RecruitmentApplication, b: RecruitmentApplication) =>
      new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime();

    return [...filtered].sort((a, b) => {
      if (sortBy === "latest") return byLatest(a, b);

      const aScore = a.aiScore;
      const bScore = b.aiScore;
      if (aScore == null && bScore == null) return byLatest(a, b);
      if (aScore == null) return 1;
      if (bScore == null) return -1;

      const scoreDiff = sortBy === "ai_desc" ? bScore - aScore : aScore - bScore;
      return scoreDiff || byLatest(a, b);
    });
  }, [applications, aiScoreFilter, sortBy]);

  const allVisibleIds = visibleApplications.map((a) => a.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Select value={filterPosition || "__none__"} onValueChange={(v) => setFilterPosition(v === "__none__" ? "" : v)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("recruitment.applications.allPositions")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t("recruitment.applications.allPositions")}</SelectItem>
            {positions.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatus || "__none__"} onValueChange={(v) => setFilterStatus(v === "__none__" ? "" : v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t("recruitment.applications.allStatuses")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t("recruitment.applications.allStatuses")}</SelectItem>
            {APP_STATUS_VALUES.map((v) => (
              <SelectItem key={v} value={v}>{t(`recruitment.applications.statuses.${v}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={aiScoreFilter} onValueChange={(v) => setAiScoreFilter(v as AiScoreFilter)}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder={t("recruitment.applications.aiScoreFilter")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("recruitment.applications.aiFilters.all")}</SelectItem>
            <SelectItem value="excellent">{t("recruitment.applications.aiFilters.excellent")}</SelectItem>
            <SelectItem value="good">{t("recruitment.applications.aiFilters.good")}</SelectItem>
            <SelectItem value="below_60">{t("recruitment.applications.aiFilters.belowSixty")}</SelectItem>
            <SelectItem value="not_analyzed">{t("recruitment.applications.aiFilters.notAnalysed")}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={(v) => setSortBy(v as ApplicationSort)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("recruitment.applications.sortBy")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="latest">{t("recruitment.applications.sort.latest")}</SelectItem>
            <SelectItem value="ai_desc">{t("recruitment.applications.sort.aiHighest")}</SelectItem>
            <SelectItem value="ai_asc">{t("recruitment.applications.sort.aiLowest")}</SelectItem>
          </SelectContent>
        </Select>

        {canManuallyAddCandidate && (
          <Button className="sm:ml-auto" onClick={() => setAddCandidateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("recruitment.applications.manual.add")}
          </Button>
        )}
      </div>

      {/* Bulk action bar — visible when ≥1 rows are checked */}
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <CheckSquare className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-44 h-8 text-sm">
              <SelectValue placeholder="Set status…" />
            </SelectTrigger>
            <SelectContent>
              {APP_STATUS_VALUES.map((v) => (
                <SelectItem key={v} value={v}>{t(`recruitment.applications.statuses.${v}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!bulkStatus || bulkStatusMutation.isPending}
            onClick={() => bulkStatusMutation.mutate()}
          >
            {bulkStatusMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Apply
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setSelectedIds(new Set()); setBulkStatus(""); }}
          >
            Clear
          </Button>
        </div>
      )}

      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-x-auto">
        <TooltipProvider delayDuration={200}>
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
              <TableHead className="pl-4 w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) =>
                    setSelectedIds(checked ? new Set(allVisibleIds) : new Set())
                  }
                />
              </TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("recruitment.applications.candidate")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("recruitment.applications.email")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("recruitment.applications.position")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("recruitment.applications.status")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell w-[260px]">{t("recruitment.applications.aiMatch")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">{t("recruitment.applications.appliedAt")}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
                </TableCell>
              </TableRow>
            ) : applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                  {t("recruitment.applications.noApplications")}
                </TableCell>
              </TableRow>
            ) : visibleApplications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                  {t("recruitment.applications.noMatchingApplications")}
                </TableCell>
              </TableRow>
            ) : (
              visibleApplications.map((app) => {
                const aiSummary = getAiScoreSummary(app.aiScoreDetail);
                const isRecommended = app.aiScore != null && app.aiScore >= 80;

                return (
                <TableRow
                  key={app.id}
                  className={`border-b border-border/40 transition-colors cursor-pointer ${
                    isRecommended
                      ? "bg-emerald-50/40 hover:bg-emerald-50/70 dark:bg-emerald-950/10 dark:hover:bg-emerald-950/20"
                      : "hover:bg-muted/40"
                  }`}
                  onClick={() => openApplication(app.id)}
                >
                  <TableCell className="pl-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(app.id)}
                      onCheckedChange={(checked) => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(app.id); else next.delete(app.id);
                          return next;
                        });
                      }}
                    />
                  </TableCell>
                  <TableCell className="py-3">
                    <p className="text-sm font-medium">{app.firstName} {app.lastName}</p>
                    <div className="mt-2 md:hidden">
                      <AiMatchIndicator score={app.aiScore} summary={aiSummary} compact />
                    </div>
                  </TableCell>
                  <TableCell className="py-3 hidden md:table-cell">
                    <span className="text-sm text-muted-foreground">{app.email}</span>
                  </TableCell>
                  <TableCell className="py-3 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground">{app.positionTitle ?? "—"}</span>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(app.status)}`}>
                        {t(`recruitment.applications.statuses.${app.status}`, { defaultValue: app.status })}
                      </span>
                      {app.status === "interview" && (
                        <p className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {app.latestInterviewScheduledAt
                            ? t(
                                app.latestInterviewStatus === "scheduled" && new Date(app.latestInterviewScheduledAt).getTime() < Date.now()
                                  ? "recruitment.applications.scheduling.waitingForFeedbackAt"
                                  : "recruitment.applications.scheduling.scheduledAt",
                                {
                                  date: new Date(app.latestInterviewScheduledAt).toLocaleString(
                                    i18n.language === "mk" ? "mk-MK" : "en-GB",
                                    { dateStyle: "medium", timeStyle: "short" },
                                  ),
                                },
                              )
                            : t("recruitment.applications.scheduling.needsScheduling")}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 hidden md:table-cell">
                    <AiMatchIndicator score={app.aiScore} summary={aiSummary} />
                  </TableCell>
                  <TableCell className="py-3 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground">
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 w-12" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end pr-2">
                      <button
                        onClick={() => { setDeleteAppId(app.id); setDeleteConfirmed(false); }}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete candidate data"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </TooltipProvider>
      </div>

      {selectedAppId && (
        <CandidateProfileSheet
          applicationId={selectedAppId}
          open={detailOpen}
          onOpenChange={(open) => {
            if (open) {
              setDetailOpen(true);
            } else {
              closeApplication();
            }
          }}
        />
      )}

      {canManuallyAddCandidate && (
        <AddCandidateDialog
          open={addCandidateOpen}
          onOpenChange={setAddCandidateOpen}
          positions={positions}
          onCreated={(application) => {
            queryClient.invalidateQueries({ queryKey: ["recruitment-applications"] });
            queryClient.invalidateQueries({ queryKey: ["recruitment-dashboard-stats"] });
            queryClient.invalidateQueries({ queryKey: ["pending-recruitment-count"] });
            openApplication(application.id);
          }}
        />
      )}

      {/* Delete candidate confirmation dialog */}
      <Dialog open={!!deleteAppId} onOpenChange={(v) => { if (!v) { setDeleteAppId(null); setDeleteConfirmed(false); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <Trash2 className="w-4 h-4" /> Delete Candidate Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This will permanently delete the application, resume, all answers, and interview records. This action cannot be undone.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteConfirmed}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              <span className="text-xs">I understand this is a permanent, irreversible action.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteAppId(null); setDeleteConfirmed(false); }}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!deleteConfirmed || deleteMutation.isPending}
              onClick={() => deleteAppId && deleteMutation.mutate(deleteAppId)}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              {deleteMutation.isPending ? "Deleting…" : "Delete All Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Candidate Profile Sheet ───────────────────────────────────────────────────

function StarRating({
  value,
  onChange,
  readOnly = false,
}: {
  value: number | null;
  onChange?: (v: number) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(n)}
          className={`p-0.5 transition-colors ${readOnly ? "cursor-default" : "hover:text-amber-500 cursor-pointer"}`}
        >
          <Star
            className={`h-5 w-5 ${n <= (value ?? 0) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`}
          />
        </button>
      ))}
      {value && <span className="text-sm text-muted-foreground ml-1">{value}/5</span>}
    </div>
  );
}

const RECOMMENDATION_OPTIONS = [
  { value: "strong_yes", color: "text-emerald-600" },
  { value: "yes", color: "text-green-600" },
  { value: "maybe", color: "text-amber-600" },
  { value: "no", color: "text-orange-600" },
  { value: "strong_no", color: "text-red-600" },
];

function InterviewScorecardSection({
  applicationId,
  interview,
  currentUserId,
}: {
  applicationId: number;
  interview: RecruitmentInterview;
  currentUserId: number | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: scorecard, isLoading } = useQuery({
    queryKey: ["interview-scorecard", applicationId, interview.id],
    queryFn: () => apiGetInterviewScorecard(applicationId, interview.id),
  });

  const [form, setForm] = useState<{
    communication: number | null;
    technical: number | null;
    problemSolving: number | null;
    cultureFit: number | null;
    motivation: number | null;
    strengths: string;
    weaknesses: string;
    overallRecommendation: string;
  }>({
    communication: null,
    technical: null,
    problemSolving: null,
    cultureFit: null,
    motivation: null,
    strengths: "",
    weaknesses: "",
    overallRecommendation: "",
  });

  // Sync form when scorecard loads
  useEffect(() => {
    if (scorecard) {
      setForm({
        communication: scorecard.communication,
        technical: scorecard.technical,
        problemSolving: scorecard.problemSolving,
        cultureFit: scorecard.cultureFit,
        motivation: scorecard.motivation,
        strengths: scorecard.strengths ?? "",
        weaknesses: scorecard.weaknesses ?? "",
        overallRecommendation: scorecard.overallRecommendation ?? "",
      });
    }
  }, [scorecard]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiUpsertInterviewScorecard(applicationId, interview.id, {
        communication: form.communication,
        technical: form.technical,
        problemSolving: form.problemSolving,
        cultureFit: form.cultureFit,
        motivation: form.motivation,
        strengths: form.strengths || null,
        weaknesses: form.weaknesses || null,
        overallRecommendation: form.overallRecommendation || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interview-scorecard", applicationId, interview.id] });
      toast.success(t("recruitment.applications.interviews.scorecard.saveSuccess"));
    },
    onError: () => toast.error(t("recruitment.applications.interviews.scorecard.saveError")),
  });

  const CRITERIA = [
    { key: "communication" as const, label: t("recruitment.applications.interviews.scorecard.communication") },
    { key: "technical" as const, label: t("recruitment.applications.interviews.scorecard.technicalSkills") },
    { key: "problemSolving" as const, label: t("recruitment.applications.interviews.scorecard.problemSolving") },
    { key: "cultureFit" as const, label: t("recruitment.applications.interviews.scorecard.cultureFit") },
    { key: "motivation" as const, label: t("recruitment.applications.interviews.scorecard.motivation") },
  ];

  if (isLoading) return <div className="text-xs text-muted-foreground py-2">{t("recruitment.applications.interviews.scorecard.loading")}</div>;

  return (
    <div className="mt-4 space-y-4 border-t pt-4">
      <h5 className="text-sm font-semibold text-foreground">{t("recruitment.applications.interviews.scorecard.title")}</h5>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CRITERIA.map(({ key, label }) => (
          <div key={key} className="space-y-1">
            <label className="text-xs text-muted-foreground font-medium">{label}</label>
            <StarRating
              value={form[key]}
              onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">{t("recruitment.applications.interviews.scorecard.strengths")}</label>
          <Textarea
            value={form.strengths}
            onChange={(e) => setForm((f) => ({ ...f, strengths: e.target.value }))}
            placeholder={t("recruitment.applications.interviews.scorecard.strengthsPlaceholder")}
            className="min-h-[80px] text-sm"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">{t("recruitment.applications.interviews.scorecard.areasForImprovement")}</label>
          <Textarea
            value={form.weaknesses}
            onChange={(e) => setForm((f) => ({ ...f, weaknesses: e.target.value }))}
            placeholder={t("recruitment.applications.interviews.scorecard.areasForImprovementPlaceholder")}
            className="min-h-[80px] text-sm"
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground font-medium">{t("recruitment.applications.interviews.scorecard.overallRecommendation")}</label>
        <Select
          value={form.overallRecommendation}
          onValueChange={(v) => setForm((f) => ({ ...f, overallRecommendation: v }))}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("recruitment.applications.interviews.scorecard.select")} />
          </SelectTrigger>
          <SelectContent>
            {RECOMMENDATION_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <span className={opt.color}>{t(`recruitment.applications.interviews.scorecard.recommendations.${opt.value}`)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
        {saveMutation.isPending
          ? t("recruitment.applications.interviews.scorecard.saving")
          : t("recruitment.applications.interviews.scorecard.save")}
      </Button>
    </div>
  );
}

function CandidateProfileSheet({
  applicationId,
  open,
  onOpenChange,
}: {
  applicationId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ── Existing state ───────────────────────────────────────────────────────
  const [notes, setNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);
  const [interviewEmployeeId, setInterviewEmployeeId] = useState("");
  const [interviewNotes, setInterviewNotes] = useState("");
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  // ── Profile tab state ────────────────────────────────────────────────────
  const [skillInput, setSkillInput] = useState("");
  const [profileForm, setProfileForm] = useState<{
    skills: string[];
    technicalKnowledge: string;
    experienceSummary: string;
    personalityNotes: string;
    overallRating: number | null;
    recommendation: string;
  }>({
    skills: [],
    technicalKnowledge: "",
    experienceSummary: "",
    personalityNotes: "",
    overallRating: null,
    recommendation: "",
  });
  const [newComment, setNewComment] = useState("");
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertForm, setConvertForm] = useState<{
    jobTitle: string;
    startDate: string;
    salary: string;
    departmentId: string;
  }>({ jobTitle: "", startDate: "", salary: "", departmentId: "" });
  const [preboardingDialogOpen, setPreboardingDialogOpen] = useState(false);
  const [preboardingDocs, setPreboardingDocs] = useState<Array<{ name: string; file: File | null }>>([]);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: app, isLoading } = useQuery({
    queryKey: ["recruitment-application", applicationId],
    queryFn: () => apiGetRecruitmentApplication(applicationId),
    enabled: open,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    enabled: open,
  });

  const { data: emailTemplates = [] } = useQuery({
    queryKey: ["recruitment-email-templates"],
    queryFn: apiGetRecruitmentEmailTemplates,
    enabled: open,
  });

  const { data: candidateProfile } = useQuery({
    queryKey: ["candidate-profile", applicationId],
    queryFn: () => apiGetCandidateProfile(applicationId),
    enabled: open,
  });

  const { data: comments = [] } = useQuery({
    queryKey: ["profile-comments", applicationId],
    queryFn: () => apiGetProfileComments(applicationId),
    enabled: open,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
    enabled: convertDialogOpen,
  });

  // Sync profile form when profile data loads
  useEffect(() => {
    if (candidateProfile) {
      setProfileForm({
        skills: candidateProfile.skills ?? [],
        technicalKnowledge: candidateProfile.technicalKnowledge ?? "",
        experienceSummary: candidateProfile.experienceSummary ?? "",
        personalityNotes: candidateProfile.personalityNotes ?? "",
        overallRating: candidateProfile.overallRating,
        recommendation: candidateProfile.recommendation ?? "",
      });
    }
  }, [candidateProfile]);

  // Sync notes when app loads
  if (app && notes === "" && app.notes) setNotes(app.notes);

  // ── Mutations ────────────────────────────────────────────────────────────
  const notesMutation = useMutation({
    mutationFn: (n: string) => apiUpdateRecruitmentApplicationNotes(applicationId, n),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-application", applicationId] });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: (data: { status: string; templateId?: number }) =>
      apiUpdateRecruitmentApplicationStatus(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-application", applicationId] });
      queryClient.invalidateQueries({ queryKey: ["recruitment-applications"] });
      toast.success(t("recruitment.applications.status") + " updated");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const interviewMutation = useMutation({
    mutationFn: (data: { employeeId: number; notes?: string }) =>
      apiAssignRecruitmentInterview(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-application", applicationId] });
      setInterviewEmployeeId("");
      setInterviewNotes("");
      toast.success(t("recruitment.applications.interviews.assignSuccess"));
    },
    onError: () => toast.error(t("recruitment.applications.interviews.assignError")),
  });

  const deleteInterviewMutation = useMutation({
    mutationFn: (interviewId: number) => apiDeleteRecruitmentInterview(applicationId, interviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-application", applicationId] });
      toast.success(t("recruitment.applications.interviews.removeSuccess"));
    },
    onError: () => toast.error(t("recruitment.applications.interviews.removeError")),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => apiAnalyzeApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-application", applicationId] });
      queryClient.invalidateQueries({ queryKey: ["recruitment-applications"] });
      toast.success("CV analysis complete");
    },
    onError: (err: any) => toast.error(err.message ?? "Analysis failed"),
  });

  const downloadResumeMutation = useMutation({
    mutationFn: () => apiDownloadRecruitmentResume(applicationId),
    onError: (err: any) => toast.error(err.message ?? "CV download failed"),
  });

  const profileMutation = useMutation({
    mutationFn: () =>
      apiUpsertCandidateProfile(applicationId, {
        skills: profileForm.skills,
        technicalKnowledge: profileForm.technicalKnowledge || null,
        experienceSummary: profileForm.experienceSummary || null,
        personalityNotes: profileForm.personalityNotes || null,
        overallRating: profileForm.overallRating,
        recommendation: profileForm.recommendation || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-profile", applicationId] });
      toast.success("Profile saved");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const addCommentMutation = useMutation({
    mutationFn: () => apiAddProfileComment(applicationId, newComment.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-comments", applicationId] });
      setNewComment("");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => apiDeleteProfileComment(applicationId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-comments", applicationId] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const convertMutation = useMutation({
    mutationFn: () =>
      apiConvertApplicantToEmployee(applicationId, {
        jobTitle: convertForm.jobTitle || undefined,
        startDate: convertForm.startDate || undefined,
        salary: convertForm.salary ? Number(convertForm.salary) : undefined,
        departmentId: convertForm.departmentId ? Number(convertForm.departmentId) : undefined,
      }),
    onSuccess: (data) => {
      toast.success("Employee created successfully!");
      setConvertDialogOpen(false);
      onOpenChange(false);
      navigate(`/employees/${data.employeeId}`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const preboardingMutation = useMutation({
    mutationFn: () =>
      apiSendPreboarding(
        applicationId,
        preboardingDocs.filter((d) => d.file).map((d) => ({ name: d.name, file: d.file! })),
      ),
    onSuccess: () => {
      toast.success(t("preboarding.sendDialog.sent"));
      setPreboardingDialogOpen(false);
      setPreboardingDocs([]);
      queryClient.invalidateQueries({ queryKey: ["recruitment-application", applicationId] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const APP_STATUS_VALUES = ["new", "screening", "interview", "offer", "hired", "rejected", "withdrawn"];

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !profileForm.skills.includes(s)) {
      setProfileForm((f) => ({ ...f, skills: [...f.skills, s] }));
    }
    setSkillInput("");
  };

  const removeSkill = (skill: string) => {
    setProfileForm((f) => ({ ...f, skills: f.skills.filter((s) => s !== skill) }));
  };

  // ── Derived display helpers ───────────────────────────────────────────────
  const PIPELINE_STAGES = [
    { key: "new",       color: "bg-blue-500" },
    { key: "screening", color: "bg-violet-500" },
    { key: "interview", color: "bg-amber-500" },
    { key: "offer",     color: "bg-emerald-500" },
    { key: "hired",     color: "bg-green-600" },
  ];
  const TERMINAL_STATUSES = new Set(["rejected", "withdrawn"]);

  const currentStageIdx = app ? PIPELINE_STAGES.findIndex((s) => s.key === app.status) : -1;
  const isTerminal = app ? app.status in TERMINAL_STATUSES : false;

  const initials = app ? `${app.firstName.charAt(0)}${app.lastName.charAt(0)}`.toUpperCase() : "??";
  const avatarColors = [
    "from-violet-500 to-purple-600",
    "from-blue-500 to-indigo-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
    "from-pink-500 to-rose-600",
  ];
  const avatarGradient = app ? avatarColors[(app.firstName.charCodeAt(0) + app.lastName.charCodeAt(0)) % avatarColors.length] : avatarColors[0];

  const aiScoreColor = (score: number) =>
    score >= 70 ? "text-emerald-600" : score >= 40 ? "text-amber-600" : "text-red-500";
  const aiScoreBg = (score: number) =>
    score >= 70 ? "bg-emerald-50 border-emerald-200" : score >= 40 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-full max-w-4xl flex flex-col p-0 gap-0 overflow-hidden h-[90vh]"
          aria-describedby={undefined}
        >
          {/* Accessibility title */}
          <DialogTitle className="sr-only">
            {app ? `${app.firstName} ${app.lastName} — ${app.positionTitle}` : t("recruitment.loading")}
          </DialogTitle>

          {isLoading || !app ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              {t("recruitment.loading")}
            </div>
          ) : (
            <>
              {/* ── HERO HEADER ────────────────────────────────────────────── */}
              {/* Theme-aware: uses card/muted/border CSS variables — works in both light and dark mode */}
              <div className="shrink-0 bg-card border-b border-border relative">
                {/* Top colour accent strip */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-primary/60 to-transparent" />

                <div className="px-6 pt-5 pb-4 pr-14">
                  {/* Top row: avatar + name + score */}
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${avatarGradient} flex items-center justify-center text-white font-bold text-xl shrink-0 shadow-md`}>
                      {initials}
                    </div>

                    {/* Name & position */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <h2 className="text-xl font-bold leading-tight text-foreground">{app.firstName} {app.lastName}</h2>
                      <p className="text-sm text-muted-foreground mt-0.5 truncate">{app.positionTitle}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {t("recruitment.applications.appliedAt")} {new Date(app.appliedAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        {isTerminal && (
                          <Badge variant="secondary" className={app.status === "rejected" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" : "bg-muted text-muted-foreground"}>
                            {t(`recruitment.applications.statuses.${app.status}`)}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* AI Score — top right */}
                    <div className="shrink-0 pt-0.5">
                      {app.aiScore !== null && app.aiScore !== undefined ? (
                        <div className={`rounded-xl border px-3 py-1.5 text-center min-w-[72px] ${aiScoreBg(app.aiScore)}`}>
                          <div className={`text-2xl font-black leading-none ${aiScoreColor(app.aiScore)}`}>{app.aiScore}%</div>
                          <div className="text-[10px] text-muted-foreground font-medium mt-0.5 leading-none">{t("recruitment.applications.aiMatch")}</div>
                        </div>
                      ) : (
                        <button
                          onClick={() => analyzeMutation.mutate()}
                          disabled={analyzeMutation.isPending}
                          className="rounded-xl border border-border bg-muted hover:bg-accent px-3 py-1.5 text-center min-w-[72px] transition-colors"
                        >
                          {analyzeMutation.isPending
                            ? <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                            : <><Sparkles className="h-4 w-4 mx-auto text-amber-500 mb-0.5" /><div className="text-[10px] text-muted-foreground font-medium leading-none">{t("recruitment.applications.panel.analyse")}</div></>
                          }
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Pipeline stages */}
                  {!isTerminal && (
                    <div className="mt-4 flex items-center gap-0">
                      {PIPELINE_STAGES.map((stage, idx) => {
                        const isPast    = idx < currentStageIdx;
                        const isCurrent = idx === currentStageIdx;
                        return (
                          <button
                            key={stage.key}
                            className="flex-1 group relative pb-5"
                            onClick={() => {
                              if (stage.key !== app.status) {
                                setPendingStatus(stage.key);
                                setSendEmailDialogOpen(true);
                              }
                            }}
                          >
                            <div className={`h-1.5 w-full transition-all ${isPast || isCurrent ? stage.color : "bg-border"} ${idx === 0 ? "rounded-l-full" : ""} ${idx === PIPELINE_STAGES.length - 1 ? "rounded-r-full" : ""}`} />
                            <div className={`absolute bottom-0 left-0 right-0 text-center text-[10px] font-medium transition-colors pt-1.5 ${isCurrent ? "text-foreground font-semibold" : "text-muted-foreground group-hover:text-foreground/70"}`}>
                              {t(`recruitment.applications.statuses.${stage.key}`)}
                            </div>
                            {isCurrent && (
                              <div className={`absolute -top-[5px] left-1/2 -translate-x-1/2 h-4 w-4 rounded-full ${stage.color} ring-2 ring-card shadow-md`} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {isTerminal && (
                    <div className="mt-3">
                      <Select
                        value={app.status}
                        onValueChange={(v) => { setPendingStatus(v); setSendEmailDialogOpen(true); }}
                      >
                        <SelectTrigger className="w-44 h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {APP_STATUS_VALUES.map((v) => (
                            <SelectItem key={v} value={v}>{t(`recruitment.applications.statuses.${v}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* ── ACTION BAR ── */}
                <div className="border-t border-border bg-muted/40 px-6 py-2 flex items-center gap-2 flex-wrap">
                  <a
                    href={`mailto:${app.email}`}
                    className="inline-flex items-center gap-1.5 text-xs bg-background hover:bg-accent text-foreground border border-border px-3 py-1.5 rounded-lg transition-colors font-medium"
                  >
                    <Send className="h-3.5 w-3.5 text-muted-foreground" /> {app.email}
                  </a>
                  {app.phone && (
                    <a
                      href={`tel:${app.phone}`}
                      className="inline-flex items-center gap-1.5 text-xs bg-background hover:bg-accent text-foreground border border-border px-3 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" /> {app.phone}
                    </a>
                  )}
                  {app.linkedinUrl && (
                    <a
                      href={app.linkedinUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs bg-background hover:bg-accent text-foreground border border-border px-3 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /> LinkedIn
                    </a>
                  )}
                  {app.portfolioUrl && (
                    <a
                      href={app.portfolioUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs bg-background hover:bg-accent text-foreground border border-border px-3 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /> {t("recruitment.applications.panel.portfolio")}
                    </a>
                  )}
                  {app.resumeFileKey && (
                    <button
                      type="button"
                      onClick={() => downloadResumeMutation.mutate()}
                      disabled={downloadResumeMutation.isPending}
                      className="inline-flex items-center gap-1.5 text-xs gradient-brand text-primary-foreground px-3 py-1.5 rounded-lg transition-colors font-semibold ml-auto"
                    >
                      {downloadResumeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      {t("recruitment.applications.panel.downloadCv")}
                    </button>
                  )}
                  {app.aiScore !== null && app.aiScore !== undefined && (
                    <button
                      onClick={() => analyzeMutation.mutate()}
                      disabled={analyzeMutation.isPending}
                      className={`inline-flex items-center gap-1.5 text-xs bg-background hover:bg-accent text-muted-foreground hover:text-foreground border border-border px-3 py-1.5 rounded-lg transition-colors font-medium ${!app.resumeFileKey ? "ml-auto" : ""}`}
                    >
                      {analyzeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      {t("recruitment.applications.panel.reanalyse")}
                    </button>
                  )}
                  {app.status === "hired" && !app.convertedEmployeeId && (
                    <button
                      className="inline-flex items-center gap-1.5 text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg transition-colors font-semibold"
                      onClick={() => {
                        setConvertForm({
                          jobTitle: app.positionTitle ?? "",
                          startDate: new Date().toISOString().split("T")[0],
                          salary: "",
                          departmentId: "",
                        });
                        setConvertDialogOpen(true);
                      }}
                    >
                      <UserPlus className="h-3.5 w-3.5" /> {t("recruitment.applications.panel.convertToEmployee")}
                    </button>
                  )}
                  {app.status === "hired" && app.convertedEmployeeId && !app.preboardingCompletedAt && (
                    <button
                      className="inline-flex items-center gap-1.5 text-xs bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-lg transition-colors font-semibold"
                      onClick={() => {
                        setPreboardingDocs([]);
                        setPreboardingDialogOpen(true);
                      }}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      {app.preboardingToken ? t("preboarding.resendPreboarding") : t("preboarding.sendPreboarding")}
                    </button>
                  )}
                  {app.preboardingCompletedAt && (
                    <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-100 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg font-semibold">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t("preboarding.preboardingCompleted")}
                    </span>
                  )}
                </div>
              </div>
              {/* ── END HERO HEADER ── */}

              {/* ── TABS ── */}
              <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0">
                <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 shrink-0 px-6 overflow-x-auto overflow-y-hidden">
                  {[
                    { value: "overview",   label: t("recruitment.applications.panel.tabOverview") },
                    { value: "profile",    label: t("recruitment.applications.panel.tabAssessment") },
                    { value: "interviews", label: t("recruitment.applications.panel.tabInterviews"), badge: app.interviews.length > 0 ? app.interviews.length : null },
                    { value: "notes",      label: t("recruitment.applications.panel.tabNotes") },
                  ].map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
                    >
                      {tab.label}
                      {tab.badge != null && (
                        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                          {tab.badge}
                        </span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <ScrollArea className="flex-1 min-h-0">
                  {/* ── OVERVIEW TAB ── */}
                  <TabsContent value="overview" className="px-6 py-6 space-y-5 mt-0">

                    {/* AI Score detail card (only shown here if score exists) */}
                    {app.aiScore !== null && app.aiScore !== undefined && app.aiScoreDetail && (
                      <div className={`rounded-xl border p-4 ${aiScoreBg(app.aiScore)}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("recruitment.applications.panel.aiBreakdown")}</p>
                            <div className="flex gap-6 text-sm">
                              <div>
                                <span className="text-xs text-muted-foreground">{t("recruitment.applications.panel.keywordMatch")}</span>
                                <p className={`text-lg font-bold ${aiScoreColor(app.aiScoreDetail.keywordScore)}`}>{app.aiScoreDetail.keywordScore}%</p>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">{t("recruitment.applications.coverLetter")}</span>
                                <p className={`text-lg font-bold ${aiScoreColor(app.aiScoreDetail.coverLetterScore)}`}>{app.aiScoreDetail.coverLetterScore}%</p>
                              </div>
                              {app.aiScoreDetail.summary && (
                                <div className="flex-1">
                                  <span className="text-xs text-muted-foreground">{t("recruitment.applications.panel.saraTake")}</span>
                                  <p className="text-xs text-foreground/80 mt-0.5 leading-relaxed line-clamp-3">{app.aiScoreDetail.summary}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Cover letter */}
                    {app.coverLetter && (
                      <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("recruitment.applications.coverLetter")}</p>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/85">{app.coverLetter}</p>
                      </div>
                    )}

                    {/* Custom answers */}
                    {app.answers.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("recruitment.applications.panel.appAnswers")}</p>
                        {app.answers.map((a) => (
                          <div key={a.fieldId} className="rounded-lg border border-border/60 bg-muted/20 p-3">
                            <p className="text-xs font-semibold text-muted-foreground mb-1">{a.fieldLabel}</p>
                            {a.fileKey ? (
                              <a href={`/api/recruitment/applications/${applicationId}/answers/${a.id}/file`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                                <FileText className="h-3.5 w-3.5" /> Download file
                              </a>
                            ) : (
                              <p className="text-sm text-foreground">{a.answerText}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No content state */}
                    {!app.coverLetter && app.answers.length === 0 && (app.aiScore === null || app.aiScore === undefined) && (
                      <div className="text-center py-10 text-muted-foreground">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">{t("recruitment.applications.panel.noDetails")}</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* ── CANDIDATE PROFILE TAB ── */}
                  <TabsContent value="profile" className="px-6 py-6 space-y-5 mt-0">
                    {/* Skills */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold">{t("recruitment.applications.assessment.skills")}</label>
                      <div className="flex flex-wrap gap-2 min-h-[32px]">
                        {profileForm.skills.map((skill) => (
                          <Badge key={skill} variant="secondary" className="gap-1 text-xs">
                            {skill}
                            <button
                              type="button"
                              onClick={() => removeSkill(skill)}
                              className="ml-0.5 hover:text-destructive transition-colors"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === ",") {
                              e.preventDefault();
                              addSkill();
                            }
                          }}
                          placeholder={t("recruitment.applications.assessment.addSkillPlaceholder")}
                          className="text-sm"
                        />
                        <Button variant="outline" size="sm" onClick={addSkill} type="button">
                          {t("recruitment.applications.assessment.add")}
                        </Button>
                      </div>
                    </div>

                    {/* Technical Knowledge */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold">{t("recruitment.applications.assessment.technicalKnowledge")}</label>
                      <Textarea
                        value={profileForm.technicalKnowledge}
                        onChange={(e) => setProfileForm((f) => ({ ...f, technicalKnowledge: e.target.value }))}
                        placeholder={t("recruitment.applications.assessment.technicalKnowledgePlaceholder")}
                        className="min-h-[100px] text-sm"
                      />
                    </div>

                    {/* Experience Summary */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold">{t("recruitment.applications.assessment.experienceSummary")}</label>
                      <Textarea
                        value={profileForm.experienceSummary}
                        onChange={(e) => setProfileForm((f) => ({ ...f, experienceSummary: e.target.value }))}
                        placeholder={t("recruitment.applications.assessment.experienceSummaryPlaceholder")}
                        className="min-h-[100px] text-sm"
                      />
                    </div>

                    {/* Personality & Culture Fit */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold">{t("recruitment.applications.assessment.personalityFit")}</label>
                      <Textarea
                        value={profileForm.personalityNotes}
                        onChange={(e) => setProfileForm((f) => ({ ...f, personalityNotes: e.target.value }))}
                        placeholder={t("recruitment.applications.assessment.personalityFitPlaceholder")}
                        className="min-h-[100px] text-sm"
                      />
                    </div>

                    {/* Overall Assessment */}
                    <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4">
                      <h4 className="text-sm font-semibold">{t("recruitment.applications.assessment.overallAssessment")}</h4>
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">{t("recruitment.applications.assessment.rating")}</label>
                        <StarRating
                          value={profileForm.overallRating}
                          onChange={(v) => setProfileForm((f) => ({ ...f, overallRating: v }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">{t("recruitment.applications.assessment.recommendation")}</label>
                        <Select
                          value={profileForm.recommendation}
                          onValueChange={(v) => setProfileForm((f) => ({ ...f, recommendation: v }))}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder={t("recruitment.applications.assessment.selectRecommendation")} />
                          </SelectTrigger>
                          <SelectContent>
                            {RECOMMENDATION_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <span className={opt.color}>{t(`recruitment.applications.interviews.scorecard.recommendations.${opt.value}`)}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button onClick={() => profileMutation.mutate()} disabled={profileMutation.isPending}>
                      {profileMutation.isPending
                        ? t("recruitment.applications.assessment.saving")
                        : t("recruitment.applications.assessment.save")}
                    </Button>

                    {/* Comments */}
                    <div className="border-t pt-5 space-y-3">
                      <h4 className="text-sm font-semibold">{t("recruitment.applications.assessment.teamComments")}</h4>
                      {comments.length === 0 && (
                        <p className="text-sm text-muted-foreground">{t("recruitment.applications.assessment.noComments")}</p>
                      )}
                      <div className="space-y-3">
                        {comments.map((c) => (
                          <div key={c.id} className="flex gap-3">
                            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                              {c.userName.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold">{c.userName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(c.createdAt).toLocaleDateString(i18n.language === "mk" ? "mk-MK" : "en-GB", { day: "numeric", month: "short" })}
                                </span>
                              </div>
                              <p className="text-sm mt-0.5 text-foreground/80">{c.comment}</p>
                            </div>
                            <button
                              onClick={() => deleteCommentMutation.mutate(c.id)}
                              className="text-muted-foreground/40 hover:text-destructive transition-colors text-xs self-start mt-1"
                              title={t("recruitment.applications.assessment.deleteComment")}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder={t("recruitment.applications.assessment.commentPlaceholder")}
                          className="text-sm min-h-[72px]"
                        />
                        <Button
                          size="sm"
                          onClick={() => addCommentMutation.mutate()}
                          disabled={!newComment.trim() || addCommentMutation.isPending}
                        >
                          {t("recruitment.applications.assessment.postComment")}
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ── INTERVIEWS TAB ── */}
                  <TabsContent value="interviews" className="px-6 py-6 space-y-5 mt-0">
                    {/* Assign new interviewer */}
                    <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
                      <h4 className="text-sm font-semibold">{t("recruitment.applications.interviews.assignTitle")}</h4>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <EmployeeCombobox
                          employees={employees}
                          value={interviewEmployeeId}
                          onChange={setInterviewEmployeeId}
                          placeholder={t("recruitment.applications.selectEmployee")}
                        />
                        <Button
                          size="sm"
                          disabled={!interviewEmployeeId || interviewMutation.isPending}
                          onClick={() =>
                            interviewMutation.mutate({
                              employeeId: Number(interviewEmployeeId),
                              notes: interviewNotes || undefined,
                            })
                          }
                        >
                          {t("recruitment.applications.assign")}
                        </Button>
                      </div>
                      {interviewEmployeeId && (
                        <Textarea
                          value={interviewNotes}
                          onChange={(e) => setInterviewNotes(e.target.value)}
                          placeholder={t("recruitment.applications.interviews.notesPlaceholder")}
                          className="text-sm min-h-[60px]"
                        />
                      )}
                    </div>

                    {/* Interview cards */}
                    {app.interviews.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t("recruitment.applications.interviews.empty")}</p>
                    )}
                    {app.interviews.map((iv) => (
                      <div key={iv.id} className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1.5">
                            <p className="font-medium text-sm">{iv.assignedToName ?? t("recruitment.applications.interviews.unknownInterviewer")}</p>
                            <div className="text-xs text-muted-foreground space-y-1">
                              <p>
                                <span className="font-medium text-foreground/70">{t("recruitment.applications.scheduling.status")}:</span>{" "}
                                {iv.scheduledAt
                                  ? t(
                                      iv.status === "scheduled" && new Date(iv.scheduledAt).getTime() < Date.now()
                                        ? "recruitment.applications.scheduling.waitingForFeedback"
                                        : "recruitment.applications.scheduling.scheduled",
                                    )
                                  : t("recruitment.applications.scheduling.needsScheduling")}
                              </p>
                              {iv.scheduledAt && (
                                <p>
                                  <span className="font-medium text-foreground/70">{t("recruitment.applications.scheduling.dateTime")}:</span>{" "}
                                  {new Date(iv.scheduledAt).toLocaleString(i18n.language === "mk" ? "mk-MK" : "en-GB", { dateStyle: "medium", timeStyle: "short" })}
                                </p>
                              )}
                              {iv.location && (
                                <p><span className="font-medium text-foreground/70">{t("recruitment.applications.scheduling.location")}:</span> {iv.location}</p>
                              )}
                              {iv.meetingLink && (
                                <p>
                                  <span className="font-medium text-foreground/70">{t("recruitment.applications.interviews.meetingLink")}:</span>{" "}
                                  <a href={iv.meetingLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t("recruitment.applications.scheduling.openMeetingLink")}</a>
                                </p>
                              )}
                              {iv.availabilitySentAt && (
                                <p><span className="font-medium text-foreground/70">{t("recruitment.applications.scheduling.invitation")}:</span> {t("recruitment.applications.scheduling.sent")}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="secondary"
                              className={`text-xs ${
                                iv.status === "passed" ? "bg-emerald-100 text-emerald-800" :
                                iv.status === "failed" ? "bg-red-100 text-red-800" :
                                iv.status === "reviewed" ? "bg-blue-100 text-blue-800" :
                                "bg-muted text-muted-foreground"
                              }`}
                            >
                              {t(`recruitment.applications.interviewStatuses.${iv.status}`, { defaultValue: iv.status })}
                            </Badge>
                            <button
                              onClick={() => deleteInterviewMutation.mutate(iv.id)}
                              className="text-muted-foreground/50 hover:text-destructive transition-colors"
                              title={t("recruitment.applications.interviews.removeInterviewer")}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {iv.feedbackNotes && (
                          <div className="bg-muted/40 rounded-lg p-3">
                            <p className="text-xs font-medium text-muted-foreground mb-1">{t("recruitment.applications.interviews.feedbackNotes")}</p>
                            <p className="text-sm">{iv.feedbackNotes}</p>
                          </div>
                        )}
                        {/* Scorecard */}
                        <InterviewScorecardSection
                          applicationId={applicationId}
                          interview={iv}
                          currentUserId={null}
                        />
                      </div>
                    ))}
                  </TabsContent>

                  {/* ── NOTES TAB ── */}
                  <TabsContent value="notes" className="px-6 py-6 space-y-3 mt-0">
                    <label className="text-sm font-semibold">{t("recruitment.applications.panel.internalNotes")}</label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t("recruitment.applications.notesPlaceholder")}
                      className="min-h-[200px] text-sm"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => notesMutation.mutate(notes)}
                        disabled={notesMutation.isPending}
                      >
                        {notesMutation.isPending ? t("recruitment.saving") : t("recruitment.save")}
                      </Button>
                      {notesSaved && <span className="text-xs text-emerald-600">Saved!</span>}
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Email on status change */}
      {app && sendEmailDialogOpen && pendingStatus && (
        <SendEmailDialog
          open={sendEmailDialogOpen}
          onOpenChange={setSendEmailDialogOpen}
          application={app}
          templates={emailTemplates}
          newStatus={pendingStatus}
          onConfirm={(templateId) => {
            statusMutation.mutate({ status: pendingStatus, templateId });
            setSendEmailDialogOpen(false);
            setPendingStatus(null);
          }}
          onSkip={() => {
            statusMutation.mutate({ status: pendingStatus });
            setSendEmailDialogOpen(false);
            setPendingStatus(null);
          }}
        />
      )}

      {/* Convert to Employee dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Convert to Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Create an employee account for <strong>{app?.firstName} {app?.lastName}</strong>. They will receive login credentials via email.
            </p>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Job Title</label>
                <Input
                  value={convertForm.jobTitle}
                  onChange={(e) => setConvertForm((f) => ({ ...f, jobTitle: e.target.value }))}
                  placeholder="e.g. Senior Engineer"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Department</label>
                <Select
                  value={convertForm.departmentId}
                  onValueChange={(v) => setConvertForm((f) => ({ ...f, departmentId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d: any) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={convertForm.startDate}
                  onChange={(e) => setConvertForm((f) => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Base Salary (optional)</label>
                <Input
                  type="number"
                  value={convertForm.salary}
                  onChange={(e) => setConvertForm((f) => ({ ...f, salary: e.target.value }))}
                  placeholder="e.g. 50000"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => convertMutation.mutate()} disabled={convertMutation.isPending}>
              {convertMutation.isPending ? "Creating…" : "Create Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-boarding dialog */}
      <Dialog open={preboardingDialogOpen} onOpenChange={setPreboardingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("preboarding.sendDialog.title")}</DialogTitle>
            <p className="text-sm text-muted-foreground">{t("preboarding.sendDialog.subtitle")}</p>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs font-medium text-muted-foreground">{t("preboarding.sendDialog.optionalDocs")}</p>
            <div className="space-y-2">
              {preboardingDocs.map((doc, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    placeholder={t("preboarding.sendDialog.documentName")}
                    value={doc.name}
                    onChange={(e) => {
                      const updated = [...preboardingDocs];
                      updated[i] = { ...updated[i], name: e.target.value };
                      setPreboardingDocs(updated);
                    }}
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-1 text-xs border border-border rounded-md px-2 py-1.5 hover:bg-muted transition-colors">
                      {doc.file ? doc.file.name.slice(0, 16) + (doc.file.name.length > 16 ? "…" : "") : t("preboarding.sendDialog.chooseFile")}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.png,.jpg"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        const updated = [...preboardingDocs];
                        updated[i] = { ...updated[i], file: f };
                        setPreboardingDocs(updated);
                      }}
                    />
                  </label>
                  <Button variant="ghost" size="icon" onClick={() => setPreboardingDocs((prev) => prev.filter((_, j) => j !== i))}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            {preboardingDocs.length < 5 && (
              <button
                type="button"
                className="text-sm text-primary hover:underline"
                onClick={() => setPreboardingDocs((prev) => [...prev, { name: "", file: null }])}
              >
                {t("preboarding.sendDialog.addDocument")}
              </button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreboardingDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={() => preboardingMutation.mutate()} disabled={preboardingMutation.isPending}>
              {preboardingMutation.isPending ? t("preboarding.sendDialog.sending") : t("preboarding.sendDialog.sendInvitation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Send Email Dialog ─────────────────────────────────────────────────────────

function SendEmailDialog({
  open, onOpenChange, application, templates, newStatus, onConfirm, onSkip,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  application: RecruitmentApplicationDetail;
  templates: RecruitmentEmailTemplate[];
  newStatus: string;
  onConfirm: (templateId?: number) => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const autoMatch = templates.find((tmpl) => tmpl.triggerStatus === newStatus);
  const effectiveId = selectedTemplateId || (autoMatch ? String(autoMatch.id) : "");
  const selectedTemplate = templates.find((tmpl) => String(tmpl.id) === effectiveId) ?? null;
  const statusLabel = t(`recruitment.applications.statuses.${newStatus}`, { defaultValue: newStatus });
  const preview = selectedTemplate ? resolvePreview(selectedTemplate, application, application.positionTitle ?? "", statusLabel) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("recruitment.sendEmail.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {t("recruitment.sendEmail.statusChangingTo")}{" "}
            <span className="font-medium text-foreground">{statusLabel}</span>.{" "}
            {t("recruitment.sendEmail.sendingTo")}{" "}
            <span className="font-medium text-foreground">{application.firstName} {application.lastName}</span>.
          </p>

          <div className="space-y-1.5">
            <Label>{t("recruitment.sendEmail.selectTemplate")}</Label>
            <Select value={effectiveId || "__none__"} onValueChange={(v) => setSelectedTemplateId(v === "__none__" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t("recruitment.sendEmail.noEmail")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("recruitment.sendEmail.noEmail")}</SelectItem>
                {templates.map((tmpl) => (
                  <SelectItem key={tmpl.id} value={String(tmpl.id)}>
                    {tmpl.name}{tmpl.triggerStatus === newStatus ? ` ${t("recruitment.emailTemplates.autoMatch")}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {preview && (
            <div className="rounded-md border p-3 space-y-2 bg-muted/30 text-sm">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{t("recruitment.sendEmail.previewLabel")}</p>
              <p><span className="text-muted-foreground">{t("recruitment.emailTemplates.subjectPreviewLabel")}</span> {preview.subject}</p>
              <div className="text-muted-foreground text-xs mt-1 max-h-28 overflow-y-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.body) }} />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onSkip}>{t("recruitment.sendEmail.skip")}</Button>
          <Button onClick={() => onConfirm(effectiveId ? Number(effectiveId) : undefined)}>
            <Send className="h-4 w-4 mr-2" />
            {effectiveId ? t("recruitment.sendEmail.send") : t("recruitment.sendEmail.updateOnly")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Email Templates Tab ───────────────────────────────────────────────────────

function EmailTemplatesTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RecruitmentEmailTemplate | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["recruitment-email-templates"],
    queryFn: apiGetRecruitmentEmailTemplates,
  });

  const deleteMutation = useMutation({
    mutationFn: apiDeleteRecruitmentEmailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recruitment-email-templates"] });
      toast.success(t("recruitment.emailTemplates.title"));
    },
    onError: (err: any) => toast.error(err.message),
  });

  const APP_STATUS_VALUES = ["new", "screening", "interview", "offer", "hired", "rejected", "withdrawn"];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {t("recruitment.emailTemplates.placeholders")}
        </p>
        <Button onClick={() => { setEditingTemplate(null); setSheetOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          {t("recruitment.emailTemplates.new")}
        </Button>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
              <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("recruitment.emailTemplates.name")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">{t("recruitment.emailTemplates.subject")}</TableHead>
              <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("recruitment.emailTemplates.triggerStatus")}</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center text-sm text-muted-foreground">
                  {t("recruitment.emailTemplates.noTemplates")}
                </TableCell>
              </TableRow>
            ) : (
              templates.map((tmpl) => (
                <TableRow key={tmpl.id} className="border-b border-border/40 hover:bg-muted/40 transition-colors">
                  <TableCell className="pl-4 py-3">
                    <p className="text-sm font-medium">{tmpl.name}</p>
                  </TableCell>
                  <TableCell className="py-3 hidden md:table-cell">
                    <span className="text-sm text-muted-foreground truncate max-w-xs block">{tmpl.subject}</span>
                  </TableCell>
                  <TableCell className="py-3">
                    {tmpl.triggerStatus ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(tmpl.triggerStatus)}`}>
                        {t(`recruitment.applications.statuses.${tmpl.triggerStatus}`, { defaultValue: tmpl.triggerStatus })}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t("recruitment.emailTemplates.noTrigger")}</span>
                    )}
                  </TableCell>
                  <TableCell className="py-3 w-20">
                    <div className="flex items-center justify-end gap-0.5 pr-2">
                      <button
                        onClick={() => { setEditingTemplate(tmpl); setSheetOpen(true); }}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t("recruitment.emailTemplates.deleteConfirm", { name: tmpl.name }))) {
                            deleteMutation.mutate(tmpl.id);
                          }
                        }}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <EmailTemplateDialog open={sheetOpen} onOpenChange={setSheetOpen} template={editingTemplate} />
    </div>
  );
}

// ── Email Template Dialog ─────────────────────────────────────────────────────

function EmailTemplateDialog({
  open, onOpenChange, template,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: RecruitmentEmailTemplate | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [triggerStatus, setTriggerStatus] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);

  if (open && !initialized) {
    setInitialized(true);
    setName(template?.name ?? "");
    setSubject(template?.subject ?? "");
    setBodyHtml(template?.bodyHtml ?? "");
    setTriggerStatus(template?.triggerStatus ?? "");
  }
  if (!open && initialized) setInitialized(false);

  const createMutation = useMutation({
    mutationFn: apiCreateRecruitmentEmailTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recruitment-email-templates"] }),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof apiUpdateRecruitmentEmailTemplate>[1] }) =>
      apiUpdateRecruitmentEmailTemplate(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recruitment-email-templates"] }),
  });

  async function handleSave() {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      toast.error(`${t("recruitment.emailTemplates.name")}, ${t("recruitment.emailTemplates.subject")} ${t("recruitment.apply.required")} ${t("recruitment.emailTemplates.body")}`);
      return;
    }
    setSaving(true);
    try {
      const data = { name: name.trim(), subject: subject.trim(), bodyHtml: bodyHtml.trim(), triggerStatus: triggerStatus || null };
      if (template) await updateMutation.mutateAsync({ id: template.id, data });
      else await createMutation.mutateAsync(data);
      toast.success(template ? t("recruitment.emailTemplates.saveChanges") : t("recruitment.emailTemplates.create"));
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  const APP_STATUS_VALUES = ["new", "screening", "interview", "offer", "hired", "rejected", "withdrawn"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle>{template ? t("recruitment.emailTemplates.edit") : t("recruitment.emailTemplates.new")}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-4 space-y-4">
            <FloatField
              label={`${t("recruitment.emailTemplates.name")} *`}
              value={name}
              onChange={(v) => setName(v as string)}
              placeholder={t("recruitment.emailTemplates.namePlaceholder")}
            />

            <FloatField
              label={`${t("recruitment.emailTemplates.subject")} *`}
              value={subject}
              onChange={(v) => setSubject(v as string)}
              placeholder={`e.g. ${t("recruitment.apply.pageTitle")} {{position_title}}`}
            />

            <div className="space-y-2">
              <Label className="text-sm font-medium">{t("recruitment.emailTemplates.body")} *</Label>
              <EmailBodyEditor value={bodyHtml} onChange={setBodyHtml} />
            </div>

            <FloatWrapper label={t("recruitment.emailTemplates.triggerStatus")}>
              <Select value={triggerStatus || "__none__"} onValueChange={(v) => setTriggerStatus(v === "__none__" ? "" : v)}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                  <SelectValue placeholder={t("recruitment.emailTemplates.noTrigger")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("recruitment.emailTemplates.noTrigger")}</SelectItem>
                  {APP_STATUS_VALUES.map((v) => (
                    <SelectItem key={v} value={v}>{t(`recruitment.applications.statuses.${v}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FloatWrapper>
            <p className="-mt-2 text-xs text-muted-foreground">{t("recruitment.emailTemplates.triggerStatusDesc")}</p>
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("recruitment.emailTemplates.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t("recruitment.emailTemplates.saving") : template ? t("recruitment.emailTemplates.saveChanges") : t("recruitment.emailTemplates.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── AI match helpers ──────────────────────────────────────────────────────────
function getAiScoreSummary(detail: RecruitmentApplication["aiScoreDetail"]) {
  const summary = detail?.summary;
  return typeof summary === "string" && summary.trim() ? summary.trim() : null;
}

function getAiMatchMeta(
  score: number | null | undefined,
  t: (key: string) => string,
) {
  if (score == null) {
    return {
      label: t("recruitment.applications.aiMatchLabels.notAnalysed"),
      badgeClass: "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300",
      barClass: "bg-slate-300 dark:bg-slate-700",
    };
  }

  if (score >= 80) {
    return {
      label: t("recruitment.applications.aiMatchLabels.excellent"),
      badgeClass: "border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/30 dark:text-emerald-300",
      barClass: "bg-emerald-500",
    };
  }

  if (score >= 60) {
    return {
      label: t("recruitment.applications.aiMatchLabels.good"),
      badgeClass: "border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-900/40 dark:bg-sky-900/30 dark:text-sky-300",
      barClass: "bg-sky-500",
    };
  }

  if (score >= 40) {
    return {
      label: t("recruitment.applications.aiMatchLabels.mediumWeak"),
      badgeClass: "border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/30 dark:text-amber-300",
      barClass: "bg-amber-500",
    };
  }

  return {
    label: t("recruitment.applications.aiMatchLabels.low"),
    badgeClass: "border-red-200 bg-red-100 text-red-800 dark:border-red-900/40 dark:bg-red-900/30 dark:text-red-300",
    barClass: "bg-red-500",
  };
}

function AiMatchIndicator({
  score,
  summary,
  compact = false,
}: {
  score: number | null | undefined;
  summary?: string | null;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const hasScore = score != null;
  const scoreValue = hasScore ? Math.round(Math.max(0, Math.min(100, score))) : 0;
  const meta = getAiMatchMeta(score, (key) => t(key));
  const isRecommended = hasScore && scoreValue >= 80;

  return (
    <div className={compact ? "max-w-[220px]" : "max-w-[260px]"}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${meta.badgeClass}`}>
          {hasScore ? `${scoreValue}%` : meta.label}
        </span>
        {hasScore && (
          <span className="text-xs font-medium text-foreground">
            {meta.label}
          </span>
        )}
        {isRecommended && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
            <Sparkles className="h-3 w-3" />
            {t("recruitment.applications.recommended")}
          </span>
        )}
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${meta.barClass}`}
          style={{ width: `${scoreValue}%` }}
        />
      </div>
      {summary && (
        <Tooltip>
          <TooltipTrigger asChild>
            <p className={`mt-1.5 truncate text-left text-xs text-muted-foreground ${compact ? "max-w-[190px]" : "max-w-[250px]"}`}>
              {summary}
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs leading-relaxed">
            {summary}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
